import { enqueueSnackbar } from "notistack";

import { updateAttachments } from "fond/redux/attachments";
import { AppThunkDispatch, Attachment, BaseAttachment } from "fond/types";
import { useAppDispatch } from "fond/utils/hooks";
import { RenameModal } from "fond/widgets";
import { ModalCloseHandler } from "fond/widgets/Modal";
import { IRenameFormData } from "fond/widgets/RenameModal";

type RenameAttachmentModalProps = {
  /**
   * The attachment to rename
   */
  attachment: BaseAttachment | Attachment;
  /**
   * An array of items to validate the new name against. If provided, the `validateUniqueName` prop must also be provided
   * otherwise the validation will not run.
   */
  items?: Attachment[] | null;
  /**
   * If true, the modal will validate that the new name is unique within the given set of items.
   * When provided, the `items` prop must also be provided otherwise the validation will not run.
   */
  validateUniqueName?: boolean;
  /**
   * Callback function to handle the onClose of the modal
   */
  onClose: ModalCloseHandler<string>;
  /**
   * The rename handler for the attachment. If not provided, a {@link defaultRenameHandler | default handler} will be used
   * which needs to take an {@link Attachment} object and will update the attachment name in the redux store.
   *
   * @param formData - the form data containing the new name
   * @returns
   */
  onRename?: (formData: IRenameFormData) => Promise<void>;
};

const isAttachment = (attachment: BaseAttachment | Attachment): attachment is Attachment => {
  return (attachment as Attachment).ID !== undefined;
};

/**
 * A default rename handler for attachments. This handler will update the attachment name in the redux store
 *
 * @param dispatch
 * @param attachment
 * @returns
 */
const defaultRenameHandler = (dispatch: AppThunkDispatch, attachment: Attachment | BaseAttachment) => async (formData: IRenameFormData) => {
  if (!isAttachment(attachment)) {
    return;
  }
  try {
    await dispatch(updateAttachments([{ ...attachment, Name: formData.name }]));
    enqueueSnackbar(
      <>
        Renamed attachment: {attachment.Name}.{attachment.Extension} &rarr; {formData.name}.{attachment.Extension}
      </>
    );
  } catch (error) {
    enqueueSnackbar("Failed to rename attachment.");
  }
};

export const RenameAttachmentModal: React.FC<RenameAttachmentModalProps> = ({
  attachment,
  items,
  validateUniqueName,
  onClose,
  onRename,
}: RenameAttachmentModalProps) => {
  const dispatch = useAppDispatch();
  const handleValidate = (value: string): string | undefined => {
    if (!validateUniqueName) return undefined;
    if (!items) return undefined;
    const isChanged = value !== attachment.Name;
    const nameExists = items?.find((item) => item.Name === value && item.Extension === attachment.Extension);
    if (isChanged && nameExists) {
      return `There is already an attachment named ${value}.${attachment.Extension}.`;
    }
    return undefined;
  };

  const renameHandler = onRename ?? defaultRenameHandler(dispatch, attachment);

  return (
    <RenameModal
      title="Rename Attachment"
      defaultValue={attachment.Name}
      message="Enter a new name for this attachment"
      onRename={renameHandler}
      onClose={onClose}
      onValidate={handleValidate}
    />
  );
};
